import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Image, 
  Link2, 
  Send, 
  Save, 
  MoreHorizontal,
  Edit,
  Trash2,
  Eye
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface ContentSchedulerProps {
  isConnected: boolean;
}

interface ScheduledPost {
  id: string;
  content: string;
  scheduledFor: Date;
  status: 'scheduled' | 'published' | 'failed';
  engagement?: {
    likes: number;
    comments: number;
    shares: number;
  };
}

export const ContentScheduler = ({ isConnected }: ContentSchedulerProps) => {
  const [content, setContent] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState("");
  const [postType, setPostType] = useState("text");
  const [isScheduling, setIsScheduling] = useState(false);
  const [showNewPost, setShowNewPost] = useState(false);
  const { toast } = useToast();

  // Mock scheduled posts
  const [scheduledPosts, setScheduledPosts] = useState<ScheduledPost[]>([
    {
      id: '1',
      content: "Excited to share insights about the future of recruiting! AI is transforming how we connect talent with opportunities. What trends are you seeing in your industry? #Recruiting #AI #FutureOfWork",
      scheduledFor: new Date(Date.now() + 24 * 60 * 60 * 1000),
      status: 'scheduled'
    },
    {
      id: '2',
      content: "Just wrapped up an amazing interview with a talented software engineer. The energy and passion they brought to the conversation was inspiring! 💪 #Hiring #TechTalent",
      scheduledFor: new Date(Date.now() - 2 * 60 * 60 * 1000),
      status: 'published',
      engagement: { likes: 23, comments: 5, shares: 2 }
    }
  ]);

  const handleSchedulePost = async () => {
    if (!content.trim() || !selectedDate || !selectedTime) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields to schedule your post.",
        variant: "destructive",
      });
      return;
    }

    setIsScheduling(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const scheduledDateTime = new Date(selectedDate);
      const [hours, minutes] = selectedTime.split(':');
      scheduledDateTime.setHours(parseInt(hours), parseInt(minutes));

      const newPost: ScheduledPost = {
        id: Date.now().toString(),
        content,
        scheduledFor: scheduledDateTime,
        status: 'scheduled'
      };

      setScheduledPosts(prev => [...prev, newPost]);
      
      // Reset form
      setContent("");
      setSelectedDate(undefined);
      setSelectedTime("");
      setShowNewPost(false);
      
      toast({
        title: "Post Scheduled",
        description: `Your post has been scheduled for ${format(scheduledDateTime, "PPP 'at' p")}.`,
      });
    } catch (error) {
      toast({
        title: "Scheduling Failed",
        description: "Failed to schedule your post. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsScheduling(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Badge variant="secondary">Scheduled</Badge>;
      case 'published':
        return <Badge variant="default" className="bg-green-100 text-green-800">Published</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  if (!isConnected) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <CalendarIcon className="h-12 w-12 text-muted-foreground" />
            <div>
              <h3 className="text-lg font-semibold">Connect LinkedIn First</h3>
              <p className="text-muted-foreground">Connect your LinkedIn account to start scheduling posts</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with New Post Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Content Scheduler</h2>
          <p className="text-muted-foreground">Schedule and manage your LinkedIn posts</p>
        </div>
        <Dialog open={showNewPost} onOpenChange={setShowNewPost}>
          <DialogTrigger asChild>
            <Button>
              <Send className="h-4 w-4 mr-2" />
              New Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Schedule New Post</DialogTitle>
              <DialogDescription>
                Create and schedule a new LinkedIn post
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Post Type */}
              <div className="space-y-2">
                <Label>Post Type</Label>
                <Select value={postType} onValueChange={setPostType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text Only</SelectItem>
                    <SelectItem value="image">Text + Image</SelectItem>
                    <SelectItem value="link">Text + Link</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Content */}
              <div className="space-y-2">
                <Label>Content</Label>
                <Textarea
                  placeholder="What's on your mind? Share insights, updates, or engage with your network..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="min-h-32"
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{content.length}/3000 characters</span>
                  <span>Tip: Posts with questions get 50% more engagement</span>
                </div>
              </div>

              {/* Additional fields based on post type */}
              {postType === "image" && (
                <div className="space-y-2">
                  <Label>Image</Label>
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                    <Image className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Click to upload an image</p>
                  </div>
                </div>
              )}

              {postType === "link" && (
                <div className="space-y-2">
                  <Label>Link URL</Label>
                  <Input placeholder="https://example.com" />
                </div>
              )}

              {/* Scheduling */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !selectedDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowNewPost(false)}>
                  Cancel
                </Button>
                <Button variant="outline">
                  <Save className="h-4 w-4 mr-2" />
                  Save Draft
                </Button>
                <Button onClick={handleSchedulePost} disabled={isScheduling}>
                  {isScheduling ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Scheduling...
                    </>
                  ) : (
                    <>
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Schedule Post
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Scheduled Posts */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Scheduled & Published Posts</h3>
        
        {scheduledPosts.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <CalendarIcon className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">No posts scheduled yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {scheduledPosts.map((post) => (
              <Card key={post.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(post.status)}
                        <span className="text-sm text-muted-foreground">
                          {post.status === 'scheduled' 
                            ? `Scheduled for ${format(post.scheduledFor, "PPP 'at' p")}`
                            : `Published on ${format(post.scheduledFor, "PPP 'at' p")}`
                          }
                        </span>
                      </div>
                      
                      <p className="text-sm line-clamp-2">{post.content}</p>
                      
                      {post.engagement && (
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>👍 {post.engagement.likes}</span>
                          <span>💬 {post.engagement.comments}</span>
                          <span>🔄 {post.engagement.shares}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                      {post.status === 'scheduled' && (
                        <>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};